'use client';
import React from 'react';
import Link from 'next/link';
import AppLogo from '@/components/ui/AppLogo';
import { LayoutDashboard, Upload, List, BrainCircuit, Wallet, Briefcase, PiggyBank, CreditCard, TrendingUp, Settings, LogOut, Shield, FileBarChart, Landmark, ShieldCheck, Receipt, PieChart, ScanLine, HelpCircle } from 'lucide-react';
import { useAuth } from '@/lib/AuthContext';
import { useRouter } from 'next/navigation';

interface SidebarProps {
  collapsed: boolean;
  mobileOpen: boolean;
  onMobileClose: () => void;
  currentPath: string;
}

const navGroups = [
  {
    label: 'Overview',
    items: [
      { label: 'Dashboard', icon: LayoutDashboard, href: '/', badge: null },
    ],
  },
  {
    label: 'Data',
    items: [
      { label: 'Upload Center', icon: Upload, href: '/upload-center', badge: null },
      { label: 'Transaction Ledger', icon: List, href: '/transaction-ledger', badge: null },
      { label: 'Data Quality', icon: ShieldCheck, href: '/data-quality', badge: null },
      { label: 'Receipt Management', icon: ScanLine, href: '/receipt-management', badge: null },
    ],
  },
  {
    label: 'Intelligence',
    items: [
      { label: 'AI Analyst', icon: BrainCircuit, href: '/ai-financial-analyst', badge: null },
    ],
  },
  {
    label: 'Accounts',
    items: [
      { label: 'Account Balances', icon: Landmark, href: '/account-balances', badge: null },
    ],
  },
  {
    label: 'Budgets',
    items: [
      { label: 'Personal Budget', icon: Wallet, href: '/personal-budget', badge: null },
      { label: 'Business Budget', icon: Briefcase, href: '/business-budget', badge: null },
      { label: 'Bills & Due Dates', icon: Receipt, href: '/bills-due-dates', badge: null },
    ],
  },
  {
    label: 'Planning',
    items: [
      { label: 'Financial Allocation', icon: PieChart, href: '/financial-allocation', badge: null },
      { label: 'Savings Finder', icon: PiggyBank, href: '/savings-finder', badge: null },
      { label: 'Debt Planner', icon: CreditCard, href: '/debt-planner', badge: null },
      { label: 'Forecast', icon: TrendingUp, href: '/forecast', badge: null },
    ],
  },
  {
    label: 'Reports',
    items: [
      { label: 'Reports & Export', icon: FileBarChart, href: '/reports', badge: null },
    ],
  },
  {
    label: 'Help',
    items: [
      { label: 'How To Use This System', icon: HelpCircle, href: '/help-training', badge: null },
    ],
  },
];

export default function Sidebar({ collapsed, mobileOpen, onMobileClose, currentPath }: SidebarProps) {
  const sidebarWidth = collapsed ? 'w-16' : 'w-60';

  return (
    <>
      {/* Desktop sidebar */}
      <aside
        className={`
          hidden lg:flex flex-col
          ${sidebarWidth} sidebar-transition
          bg-card border-r border-border
          h-screen overflow-hidden flex-shrink-0
        `}
      >
        <SidebarContent collapsed={collapsed} currentPath={currentPath} onClose={onMobileClose} />
      </aside>

      {/* Mobile sidebar */}
      <aside
        className={`
          fixed inset-y-0 left-0 z-50 flex flex-col
          w-60 bg-card border-r border-border
          sidebar-transition lg:hidden
          ${mobileOpen ? 'translate-x-0' : '-translate-x-full'}
        `}
      >
        <SidebarContent collapsed={false} currentPath={currentPath} onClose={onMobileClose} />
      </aside>
    </>
  );
}

function SidebarContent({
  collapsed,
  currentPath,
  onClose,
}: {
  collapsed: boolean;
  currentPath: string;
  onClose: () => void;
}) {
  const { signOut } = useAuth();
  const router = useRouter();

  const handleSignOut = async () => {
    await signOut();
    router.push('/sign-up-login-screen');
  };

  return (
    <div className="flex flex-col h-full">
      {/* Logo — clickable, routes to dashboard */}
      <Link
        href="/"
        onClick={onClose}
        className={`flex items-center h-16 px-4 border-b border-border flex-shrink-0 hover:bg-muted/50 transition-colors ${collapsed ? 'justify-center' : 'gap-3'}`}
        title="Go to Dashboard"
      >
        <AppLogo size={32} />
        {!collapsed && (
          <div className="flex flex-col min-w-0">
            <span className="text-xs font-700 text-foreground leading-tight truncate">Financial Freedom</span>
            <span className="text-xs text-muted-foreground leading-tight">Command Center</span>
          </div>
        )}
      </Link>

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto scrollbar-thin py-3">
        {/* Dashboard quick-link at top */}
        <div className="mb-2 px-2">
          <Link
            href="/"
            onClick={onClose}
            className={`
              flex items-center gap-3 px-3 py-2.5 rounded-lg
              transition-all duration-150 group relative
              ${currentPath === '/' ?'bg-accent/10 text-accent border-l-2 border-accent' :'text-muted-foreground hover:bg-muted hover:text-foreground border-l-2 border-transparent'
              }
              ${collapsed ? 'justify-center px-2' : ''}
            `}
            title={collapsed ? 'Dashboard' : undefined}
          >
            <LayoutDashboard size={18} className="flex-shrink-0" />
            {!collapsed && <span className="text-sm font-600 truncate flex-1">Dashboard</span>}
          </Link>
        </div>

        {navGroups.map((group) => (
          <div key={`group-${group.label}`} className="mb-4">
            {!collapsed && (
              <p className="px-4 pb-1 text-[10px] font-600 uppercase tracking-widest text-muted-foreground">
                {group.label}
              </p>
            )}
            {group.items.map((item) => {
              const isActive = currentPath === item.href;
              const ItemIcon = item.icon;
              return (
                <Link
                  key={`nav-${item.href}`}
                  href={item.href}
                  onClick={onClose}
                  className={`
                    flex items-center gap-3 mx-2 my-0.5 px-3 py-2.5 rounded-lg
                    transition-all duration-150 group relative
                    ${isActive
                      ? 'bg-accent/10 text-accent border-l-2 border-accent' :'text-muted-foreground hover:bg-muted hover:text-foreground border-l-2 border-transparent'
                    }
                    ${collapsed ? 'justify-center px-2' : ''}
                  `}
                  title={collapsed ? item.label : undefined}
                >
                  <ItemIcon size={18} className="flex-shrink-0" />
                  {!collapsed && (
                    <span className="text-sm font-500 truncate flex-1">{item.label}</span>
                  )}
                  {!collapsed && item.badge && (
                    <span className={`
                      text-[10px] font-700 px-1.5 py-0.5 rounded-full
                      ${item.badge === '!' ? 'bg-danger/20 text-danger' : 'bg-accent/20 text-accent'}
                    `}>
                      {item.badge}
                    </span>
                  )}
                </Link>
              );
            })}
          </div>
        ))}
      </nav>

      {/* Bottom */}
      <div className="border-t border-border py-3 px-2 flex-shrink-0">
        <Link
          href="/settings"
          className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground transition-all duration-150 ${collapsed ? 'justify-center' : ''}`}
          title={collapsed ? 'Settings' : undefined}
        >
          <Settings size={18} />
          {!collapsed && <span className="text-sm font-500">Settings</span>}
        </Link>
        <button
          onClick={handleSignOut}
          className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground transition-all duration-150 ${collapsed ? 'justify-center' : ''}`}
          title={collapsed ? 'Sign Out' : undefined}
        >
          <LogOut size={18} />
          {!collapsed && <span className="text-sm font-500">Sign Out</span>}
        </button>
        {!collapsed && (
          <div className="mx-3 mt-3 p-2 rounded-lg bg-muted/50 flex items-center gap-2">
            <Shield size={12} className="text-success flex-shrink-0" />
            <span className="text-[10px] text-muted-foreground">256-bit encrypted</span>
          </div>
        )}
      </div>
    </div>
  );
}